import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CollectInsurancePremiumInvoicePayload, InsuranceApiService, InsurancePremiumInvoiceDetail } from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-premium-invoice-detail',
  imports: [
    AinAmountPipe,
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    TableModule,
    DialogModule
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.premiums.invoiceDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.premiums.help.detail.title'"
        [descriptionKey]="'insurance.premiums.help.detail.description'"
        [bulletKeys]="[
          'insurance.premiums.help.detail.bullets.collect',
          'insurance.premiums.help.detail.bullets.writeOff',
          'insurance.premiums.help.detail.bullets.history'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else if (!invoice()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/premium-invoices']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            <button
              pButton
              type="button"
              [label]="t('insurance.premiums.invoiceDetail.actions.collect')"
              [disabled]="saving() || invoice()!.status === 'Paid' || invoice()!.status === 'WrittenOff'"
              (click)="openCollectDialog()"></button>
            <button
              pButton
              type="button"
              severity="danger"
              [label]="t('insurance.premiums.invoiceDetail.actions.writeOff')"
              [disabled]="saving() || invoice()!.status === 'Paid' || invoice()!.status === 'WrittenOff'"
              (click)="writeOff()"></button>
          </div>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.premiums.invoiceDetail.sections.summary') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.premiums.help.summary.title'"
              [descriptionKey]="'insurance.premiums.help.summary.description'"
              [bulletKeys]="[
                'insurance.premiums.help.summary.bullets.amounts',
                'insurance.premiums.help.summary.bullets.policy'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.premiums.invoices.fields.number') }}: <strong>{{ invoice()!.invoiceNumber }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ t(statusKey(invoice()!.status)) }}</strong></span>
            <span>{{ t('insurance.premiums.invoices.fields.policy') }}: <strong>{{ invoice()!.policyNumber }}</strong></span>
            <span>{{ t('insurance.premiums.invoices.fields.lineOfBusiness') }}: <strong>{{ invoice()!.lineOfBusiness }}</strong></span>
            <span>{{ t('insurance.premiums.invoices.fields.amount') }}: <strong>{{ invoice()!.amount + invoice()!.taxAmount | ainAmount }}</strong></span>
            <span>{{ t('insurance.premiums.invoices.fields.collected') }}: <strong>{{ invoice()!.collectedAmount | ainAmount }}</strong></span>
            <span>{{ t('insurance.premiums.invoices.fields.outstanding') }}: <strong>{{ invoice()!.outstandingAmount | ainAmount }}</strong></span>
            @if (invoice()!.paymentIntentId) {
              <span>{{ t('insurance.premiums.invoiceDetail.fields.paymentIntentId') }}: <strong>{{ invoice()!.paymentIntentId }}</strong></span>
            }
          </div>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.premiums.invoiceDetail.sections.collections') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.premiums.help.collections.title'"
              [descriptionKey]="'insurance.premiums.help.collections.description'"
              [bulletKeys]="['insurance.premiums.help.collections.bullets.trace']" />
          </div>
          @if (invoice()!.collections.length === 0) {
            <p>{{ t('insurance.premiums.invoiceDetail.states.noCollections') }}</p>
          } @else {
            <p-table [value]="invoice()!.collections" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.premiums.invoiceDetail.fields.paymentReference') }}</th>
                  <th>{{ t('insurance.premiums.invoiceDetail.fields.collectedAmount') }}</th>
                  <th>{{ t('columns.date') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.paymentReference }}</td>
                  <td>{{ row.collectedAmount | ainAmount }}</td>
                  <td>{{ row.collectedAtUtc | ainDate }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }

        <p-dialog
          [header]="t('insurance.premiums.invoiceDetail.dialog.collect')"
          [modal]="true"
          [(visible)]="collectDialogVisible"
          [style]="{ width: '560px' }">
          <div class="dialog-grid">
            <label>
              <span>{{ t('insurance.premiums.invoiceDetail.fields.collectedAmount') }}</span>
              <p-inputNumber [(ngModel)]="collectForm.collectedAmount" name="dialogCollectedAmount" [min]="0.01" />
            </label>
            <label class="wide">
              <span>{{ t('insurance.premiums.invoiceDetail.fields.paymentReference') }}</span>
              <input pInputText [(ngModel)]="collectForm.paymentReference" name="dialogPaymentReference" />
            </label>
            <label class="wide">
              <span>{{ t('insurance.premiums.invoiceDetail.fields.paymentIntentId') }}</span>
              <input
                pInputText
                [(ngModel)]="collectForm.paymentIntentId"
                name="dialogPaymentIntentId"
                [placeholder]="t('insurance.premiums.invoiceDetail.fields.paymentIntentHint')" />
            </label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="collectDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('insurance.premiums.invoiceDetail.actions.collect')" [disabled]="saving()" (click)="collect()"></button>
          </ng-template>
        </p-dialog>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    label { display: grid; gap: 6px; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .dialog-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsurancePremiumInvoiceDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly invoice = signal<InsurancePremiumInvoiceDetail | null>(null);
  readonly collectDialogVisible = signal(false);

  collectForm: CollectInsurancePremiumInvoicePayload = {
    collectedAmount: 0,
    collectedAtUtc: null,
    paymentReference: '',
    paymentIntentId: null
  };

  ngOnInit(): void {
    this.load();
  }

  pageTitle(): string {
    const current = this.invoice();
    return current
      ? `${this.t('insurance.premiums.invoiceDetail.title')} — ${current.invoiceNumber}`
      : this.t('insurance.premiums.invoiceDetail.title');
  }

  private load(): void {
    const invoiceId = this.route.snapshot.paramMap.get('invoiceId');
    if (!invoiceId) {
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    this.api.getPremiumInvoice(invoiceId).subscribe({
      next: item => {
        this.invoice.set(item);
        this.permissionDenied.set(false);
        this.error.set('');
        this.loading.set(false);
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  openCollectDialog(): void {
    const current = this.invoice();
    if (!current) {
      return;
    }
    this.collectForm = {
      collectedAmount: current.outstandingAmount,
      collectedAtUtc: null,
      paymentReference: '',
      paymentIntentId: current.paymentIntentId
    };
    this.collectDialogVisible.set(true);
  }

  collect(): void {
    const current = this.invoice();
    if (!current) {
      return;
    }
    if (!confirm(this.t('insurance.premiums.invoiceDetail.messages.collectConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.error.set('');
    const payload: CollectInsurancePremiumInvoicePayload = {
      ...this.collectForm,
      paymentReference: this.collectForm.paymentReference.trim(),
      paymentIntentId: this.collectForm.paymentIntentId?.trim() || null,
      collectedAtUtc: new Date().toISOString()
    };
    this.api.collectPremiumInvoice(current.id, payload).subscribe({
      next: item => {
        this.invoice.set(item);
        this.collectDialogVisible.set(false);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  writeOff(): void {
    const current = this.invoice();
    if (!current) {
      return;
    }
    if (!confirm(this.t('insurance.premiums.invoiceDetail.messages.writeOffConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.error.set('');
    this.api.writeOffPremiumInvoice(current.id, 'manual_write_off').subscribe({
      next: item => {
        this.invoice.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  statusKey(status: InsurancePremiumInvoiceDetail['status']): string {
    return `insurance.premiums.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
